/**
 * Getalbegrip (GET) — 50 families, 8 skills
 *
 * Skills: positiestelsel (6), breuken (13), kommagetallen (10),
 *         procenten (8), verhoudingen (4),
 *         negatieve_getallen (3), machten_wortels (3), wetenschappelijk (2)
 *
 * Split from taxonomyRegistry.ts in R23.
 * REK module migratie (17 mrt 2026): bewerkingen skill (3 families) verwijderd.
 * GET-HOOFDREKENEN → splits naar REK-HANDIG-PLUS-MIN + REK-HANDIG-KEER
 * GET-SCHATSTRATEGIE → REK-SCHATTEN
 * GET-VOLGORDE-BEWERKING → REK-VOLGORDE
 */

import type { FamilyDefinition } from '../types'

export const GET_FAMILIES: FamilyDefinition[] = [
  // 1.1 Skill: positiestelsel (6 families)
  {
    familyId: 'GET-GROOT-POS',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'groot_positiestelsel',
    tier: 1,
    levelCode: 'B6',
    description: 'Positiestelsel: waarde van cijfers (M/D/H/T/E)',
  },
  {
    familyId: 'GET-GROOT-LEZEN',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'groot_lezen_schrijven',
    tier: 1,
    levelCode: 'B6',
    description: 'Grote getallen lezen en schrijven',
  },
  {
    familyId: 'GET-GROOT-VERGELIJK',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'groot_vergelijken',
    tier: 2,
    levelCode: 'B6+',
    description: 'Grote getallen vergelijken',
  },
  {
    familyId: 'GET-GROOT-AFRONDEN',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'groot_afronden_basis',
    tier: 3,
    levelCode: 'M6',
    description: 'Afronden basis: dichtstbijzijnde tiental/honderdtal',
  },
  {
    familyId: 'GET-ROND-BASIS',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'afronden_tientallen_honderdtallen',
    tier: 5,
    levelCode: 'E6',
    description:
      'Flexibel afronden: duizendtallen, tienduizendtallen, decimalen',
  },
  {
    familyId: 'GET-GROOT-SCHATTEN',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'groot_schatten',
    tier: 7,
    levelCode: 'B7',
    description: 'Schatten en globaal rekenen',
  },
  // 1.2 Skill: breuken (13 families)
  {
    familyId: 'GET-BREUK-BASIS',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_basis_visueel',
    tier: 1,
    levelCode: 'B6',
    description: 'Breuk als deel van geheel (visueel)',
  },
  {
    familyId: 'GET-BREUK-LEZEN',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_lezen_schrijven',
    tier: 1,
    levelCode: 'B6',
    description: 'Breuken lezen en schrijven',
  },
  {
    familyId: 'GET-BREUK-GEL-PLUS',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_optellen_gel',
    tier: 2,
    levelCode: 'B6+',
    description: 'Breuken optellen gelijknamig (teller+teller, noemer blijft)',
  },
  {
    familyId: 'GET-BREUK-GEL-MIN',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_aftrekken_gel',
    tier: 2,
    levelCode: 'B6+',
    description: 'Gelijknamige breuken aftrekken',
  },
  {
    familyId: 'GET-VERGELIJK',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_vergelijken',
    tier: 3,
    levelCode: 'M6',
    description: 'Breuken vergelijken',
  },
  {
    familyId: 'GET-BREUK-GETALLENLIJN',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_getallenlijn',
    tier: 5,
    levelCode: 'E6',
    description: 'Breuken op getallenlijn (CITO essentieel)',
  },
  {
    familyId: 'GET-BREUK-VEREENVOUDIG',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_vereenvoudigen',
    tier: 5,
    levelCode: 'E6',
    description: 'Breuken vereenvoudigen',
  },
  {
    familyId: 'GET-BREUK-UITBREIDEN',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_uitbreiden',
    tier: 5,
    levelCode: 'E6',
    description: 'Breuken uitbreiden',
  },
  {
    familyId: 'GET-BREUK-ONG-PLUS',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_optellen_ong',
    tier: 7,
    levelCode: 'B7',
    description:
      'Breuken optellen ongelijknamig (eerst gelijknamig maken via KGV)',
  },
  {
    familyId: 'GET-BREUK-MIN',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_aftrekken_ong',
    tier: 7,
    levelCode: 'B7',
    description: 'Ongelijknamige breuken aftrekken',
  },
  {
    familyId: 'GET-BREUK-KEER',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_vermenigvuldigen',
    tier: 9,
    levelCode: 'M7',
    description: 'Breuken vermenigvuldigen (teller × teller, noemer × noemer)',
  },
  {
    familyId: 'GET-BREUK-DEEL',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_delen',
    tier: 13,
    levelCode: 'B8',
    description: 'Breuken delen (keer het omgekeerde)',
  },
  {
    familyId: 'GET-BREUK-GEMENGD',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_gemengd',
    tier: 11,
    levelCode: 'E7',
    description: 'Gemengde getallen',
  },
  // 1.3 Skill: kommagetallen (10 families)
  {
    familyId: 'GET-KOMMA-WAARDE',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_decimale_waarde',
    tier: 5,
    levelCode: 'E6',
    description: 'Kommagetallen: begrip van tienden/honderdsten',
  },
  {
    familyId: 'GET-KOMMA-LEZEN',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_lezen_schrijven',
    tier: 5,
    levelCode: 'E6',
    description: 'Kommagetallen lezen en schrijven',
  },
  {
    familyId: 'GET-KOMMA-VERGELIJK',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_vergelijken',
    tier: 6,
    levelCode: 'E6+',
    description: 'Kommagetallen vergelijken',
  },
  {
    familyId: 'GET-KOMMA-GETALLENLIJN',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_getallenlijn',
    tier: 6,
    levelCode: 'E6+',
    description: 'Kommagetallen op getallenlijn (CITO essentieel)',
  },
  {
    familyId: 'GET-KOMMA-PLUS',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_optellen',
    tier: 7,
    levelCode: 'B7',
    description: 'Kommagetallen optellen',
  },
  {
    familyId: 'GET-KOMMA-MIN',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_aftrekken',
    tier: 7,
    levelCode: 'B7',
    description: 'Kommagetallen aftrekken',
  },
  {
    familyId: 'GET-KOMMA-KEER',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_vermenigvuldigen',
    tier: 9,
    levelCode: 'M7',
    description: 'Kommagetallen vermenigvuldigen',
  },
  {
    familyId: 'GET-KOMMA-DEEL',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_delen',
    tier: 11,
    levelCode: 'E7',
    description: 'Kommagetallen delen',
  },
  {
    familyId: 'GET-DEEL-KOMMA',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'delen_door_kommagetal',
    tier: 11,
    levelCode: 'E7',
    description: 'Delen door kommagetal (CITO eindtoets)',
  },
  {
    familyId: 'GET-KOMMA-AFRONDEN',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_afronden',
    tier: 9,
    levelCode: 'M7',
    description: 'Kommagetallen afronden',
  },
  // 1.4 Skill: procenten (8 families + 1 samengesteld)
  {
    familyId: 'GET-PROC-BASIS',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_breuk_decimaal',
    tier: 6,
    levelCode: 'E6+',
    description:
      'Procenten basis: drieledige koppeling procent ↔ breuk ↔ decimaal',
  },
  {
    familyId: 'GET-PROC-VAN',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_van_bedrag',
    tier: 9,
    levelCode: 'M7',
    description: 'Percentage van een bedrag berekenen (1%-regel)',
  },
  {
    familyId: 'GET-VERH-BREUK-PROC-KOPPEL',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'verh_breuk_proc_koppeling',
    tier: 9,
    levelCode: 'M7',
    description: 'Breuk ↔ procent ↔ decimaal toepassen (kerndoel)',
  },
  {
    familyId: 'GET-PROC-NIEUW',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_prijsverandering',
    tier: 11,
    levelCode: 'E7',
    description: 'Korting of opslag verwerken (nieuwe prijs berekenen)',
  },
  {
    familyId: 'GET-PROC-TOENAME',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_toename',
    tier: 13,
    levelCode: 'B8',
    description: 'Procentuele toename',
  },
  {
    familyId: 'GET-PROC-AFNAME',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_afname',
    tier: 13,
    levelCode: 'B8',
    description: 'Procentuele afname',
  },
  {
    familyId: 'GET-PROC-TERUG',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_terugrekenen',
    tier: 15,
    levelCode: 'E8',
    description: 'Terugrekenen: van deel naar geheel (100%)',
  },
  {
    familyId: 'GET-PROC-BTW',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_btw_excl',
    tier: 16,
    levelCode: 'E8+',
    description: 'BTW exclusief berekenen: delen door 1.xx factor',
  },
  {
    familyId: 'GET-PROC-SAMENGESTELD',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_samengesteld',
    tier: 10,
    levelCode: 'M7+',
    description:
      'Samengestelde procentberekeningen: korting, stijging, dubbele korting, val-distractors',
  },
  // 1.6 Skill: verhoudingen (4 families)
  {
    familyId: 'GET-VERH-SCHAAL',
    module: 'Getalbegrip',
    skill: 'verhoudingen',
    subSkill: 'verhoudingstabel',
    tier: 10,
    levelCode: 'M7+',
    description: 'Verhoudingstabel: kruislings vermenigvuldigen',
  },
  {
    familyId: 'GET-VERH-SCHAAL-KAART',
    module: 'Getalbegrip',
    skill: 'verhoudingen',
    subSkill: 'verh_schaal_kaart',
    tier: 11,
    levelCode: 'E7',
    description: 'Schaal op kaarten',
  },
  {
    familyId: 'GET-VERH-RECEPT',
    module: 'Getalbegrip',
    skill: 'verhoudingen',
    subSkill: 'verh_recept',
    tier: 9,
    levelCode: 'M7',
    description: 'Verhoudingen in recepten',
  },
  {
    familyId: 'GET-VERH-VERDELEN',
    module: 'Getalbegrip',
    skill: 'verhoudingen',
    subSkill: 'verh_verdelen',
    tier: 11,
    levelCode: 'E7',
    description: 'Verdelen in verhouding',
  },
  // 1.7 Skill: negatieve_getallen (3 families)
  {
    familyId: 'GET-NEGATIEF',
    module: 'Getalbegrip',
    skill: 'negatieve_getallen',
    subSkill: 'negatief_getallenlijn',
    tier: 11,
    levelCode: 'E7',
    description: 'Negatieve getallen op getallenlijn',
  },
  {
    familyId: 'GET-NEGATIEF-PLUS',
    module: 'Getalbegrip',
    skill: 'negatieve_getallen',
    subSkill: 'negatief_optellen',
    tier: 13,
    levelCode: 'B8',
    description: 'Optellen met negatieve getallen',
  },
  {
    familyId: 'GET-NEGATIEF-MIN',
    module: 'Getalbegrip',
    skill: 'negatieve_getallen',
    subSkill: 'negatief_aftrekken',
    tier: 13,
    levelCode: 'B8',
    description: 'Aftrekken met negatieve getallen',
  },
  // 1.8 Skill: machten_wortels (3 families)
  {
    familyId: 'GET-MACHT',
    module: 'Getalbegrip',
    skill: 'machten_wortels',
    subSkill: 'macht_basis',
    tier: 13,
    levelCode: 'B8',
    description: 'Machtsverheffen basis',
  },
  {
    familyId: 'GET-MACHT-GRONDTAL',
    module: 'Getalbegrip',
    skill: 'machten_wortels',
    subSkill: 'macht_grondtal',
    tier: 14,
    levelCode: 'M8',
    description: 'Machten met grondtal',
  },
  {
    familyId: 'GET-WORTEL',
    module: 'Getalbegrip',
    skill: 'machten_wortels',
    subSkill: 'wortel_basis',
    tier: 15,
    levelCode: 'E8',
    description: 'Worteltrekken basis',
  },
  // 1.9 Skill: wetenschappelijk (2 families)
  {
    familyId: 'GET-WETENSCHAPPELIJK',
    module: 'Getalbegrip',
    skill: 'wetenschappelijk',
    subSkill: 'wetenschap_notatie',
    tier: 16,
    levelCode: 'E8+',
    description: 'Wetenschappelijke notatie',
  },
  {
    familyId: 'GET-WETENSCHAP-REKEN',
    module: 'Getalbegrip',
    skill: 'wetenschappelijk',
    subSkill: 'wetenschap_rekenen',
    tier: 16,
    levelCode: 'E8+',
    description: 'Rekenen met wetenschappelijke notatie',
  },
]
